"use client";

/**
 * Canvas that draws a scene at a given time using the exact same `drawFrame`
 * the exporter uses — so the preview can't drift from the downloaded file.
 *
 * It renders at full output resolution and is scaled down with CSS, which keeps
 * the preview crisp on high-DPI screens without a second layout pass.
 */
import { useEffect, useRef } from "react";
import { drawFrame } from "@/comparison/draw";
import type { Scene } from "@/comparison/scene";
import type { PoseKind } from "@/comparison/types";
import { cn } from "@/utils/ui";

export function SceneCanvas({
	scene,
	timeMs,
	poseOverride,
	className,
}: {
	scene: Scene | null;
	timeMs: number;
	poseOverride?: PoseKind;
	className?: string;
}) {
	const canvasRef = useRef<HTMLCanvasElement>(null);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas || !scene) return;

		const { width, height } = scene.layout.canvas;
		if (canvas.width !== width) canvas.width = width;
		if (canvas.height !== height) canvas.height = height;

		const ctx = canvas.getContext("2d", { alpha: false });
		if (!ctx) return;
		drawFrame({ ctx, scene, timeMs, poseOverride });
	}, [scene, timeMs, poseOverride]);

	return (
		<canvas
			ref={canvasRef}
			className={cn(
				"border-border block h-auto w-full rounded-xl border shadow-sm",
				className,
			)}
			style={
				scene
					? { aspectRatio: `${scene.layout.canvas.width} / ${scene.layout.canvas.height}` }
					: undefined
			}
		/>
	);
}
